// src/Components/AllComponents/BranchManagement.jsx

import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Pagination } from "rsuite";
import { toast } from "react-toastify";
import "./BranchManagement.css";
import BranchModal from "./Inventory/BranchModal";
import ConfirmDeleteModal from "./Inventory/ConfirmDeleteModal";
import {
    getBranches,
    createBranch,
    updateBranch,
    deleteBranch,
} from "../../api/admin/branch";
import { initBranchStock } from "../../api/admin/inventory";

const BranchManagement = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const limitOptions = [10, 20, 30, 50, 100];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchBranches = async () => {
        setLoading(true);
        try {
            const { data } = await getBranches();
            // The API returns { message: "...", result: [...] }
            const list = data?.result || [];
            setBranches(list);
        } catch (err) {
            console.error("Failed to load branches", err);
            toast.error("Failed to load branches. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const handleAdd = () => {
        setEditingBranch(null);
        setIsModalOpen(true);
    };

    const handleEdit = (branch) => {
        setEditingBranch(branch);
        setIsModalOpen(true);
    };

    const handleDeleteRequest = (branch) => {
        setDeleteTarget(branch);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const id = deleteTarget._id || deleteTarget.id;
            await deleteBranch(id);
            setBranches((prev) => prev.filter((b) => (b._id || b.id) !== id));
            toast.success("Branch deleted successfully!");
        } catch (err) {
            console.error("Failed to delete branch", err);
            toast.error("Failed to delete branch. Please try again.");
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleSave = async (formData) => {
        try {
            if (editingBranch) {
                const id = editingBranch._id || editingBranch.id;
                await updateBranch(id, formData);
                toast.success("Branch updated successfully!");
            } else {
                const { data } = await createBranch(formData);
                const newBranchId = data?.result?._id || data?.result?.id;
                // Seed zero-stock entries for every medicine in the new branch.
                // Failure here shouldn't roll back branch creation — just warn.
                if (newBranchId) {
                    try {
                        await initBranchStock(newBranchId);
                    } catch (initErr) {
                        console.warn("init-branch-stock failed", initErr);
                        toast.warning(
                            "Branch created, but stock initialization failed. Open Stock Management to set stock manually."
                        );
                    }
                }
                toast.success("Branch created successfully!");
            }
            setIsModalOpen(false);
            fetchBranches();
        } catch (err) {
            console.error("Failed to save branch", err);
            toast.error("Failed to save branch. Please try again.");
        }
    };

    const filteredBranches = branches.filter((b) =>
        b.name?.toLowerCase().includes(search.toLowerCase()) ||
        b.address?.toLowerCase().includes(search.toLowerCase())
    );

    const startIndex = (page - 1) * limit;
    const pageItems = filteredBranches.slice(startIndex, startIndex + limit);

    return (
        <div className="brn-surface">
            <div className="brn-shell">
                <header className="brn-hero">
                    <div className="brn-hero-copy">
                        <p className="brn-chip">Administration</p>
                        <h1>
                            Branch Management
                            <span> at a glance.</span>
                        </h1>
                    </div>
                </header>

                <section className="brn-search-block">
                    <div className="brn-search-field">
                        <svg
                            className="brn-search-icon"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search branches by name or address…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <span className="brn-count">
                        {search
                            ? `Showing ${filteredBranches.length} of ${branches.length} branches`
                            : `All branches (${branches.length})`}
                    </span>
                </section>

                <section className="brn-actions-bar">
                    <button className="brn-primary" onClick={handleAdd}>
                        <span>+</span> Add Branch
                    </button>
                </section>

                <div className="brn-table-wrapper">
                    <table className="brn-table">
                        <thead>
                            <tr>
                                <th>Branch Name</th>
                                <th>Address</th>
                                <th style={{ width: "120px" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={idx} className="brn-skeleton-row">
                                        <td><div className="brn-skeleton-cell"></div></td>
                                        <td><div className="brn-skeleton-cell"></div></td>
                                        <td>
                                            <div className="brn-skeleton-actions">
                                                <div className="brn-skeleton-button"></div>
                                                <div className="brn-skeleton-button"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="brn-empty">
                                        No branches found.
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((branch) => (
                                    <tr key={branch._id || branch.id}>
                                        <td style={{ fontWeight: 600 }}>{branch.name}</td>
                                        <td>{branch.address}</td>
                                        <td className="brn-actions">
                                            <button
                                                className="brn-btn brn-btn-edit"
                                                onClick={() => handleEdit(branch)}
                                                title="Edit Branch"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="brn-btn brn-btn-delete"
                                                onClick={() => handleDeleteRequest(branch)}
                                                title="Delete Branch"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {filteredBranches.length > limit && (
                    <div className="brn-pagination">
                        <Pagination
                            layout={["total", "-", "limit", "|", "pager", "skip"]}
                            total={filteredBranches.length}
                            limit={limit}
                            limitOptions={limitOptions}
                            activePage={page}
                            onChangePage={setPage}
                            onChangeLimit={(nextLimit) => {
                                setLimit(nextLimit);
                                setPage(1);
                            }}
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            maxButtons={5}
                            size="md"
                        />
                    </div>
                )}

                <BranchModal
                    isOpen={isModalOpen}
                    mode={editingBranch ? "edit" : "add"}
                    initialValues={editingBranch}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                />

                <ConfirmDeleteModal
                    isOpen={!!deleteTarget}
                    title="Delete Branch"
                    message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
                    onCancel={() => setDeleteTarget(null)}
                    onConfirm={handleConfirmDelete}
                    confirming={isDeleting}
                />
            </div>
        </div>
    );
};

export default BranchManagement;
